import { EApiStatus, TeacherStatus } from 'constants/common';
import { EMemberCenterErrorCode } from 'constants/memberCenter';
import { t as trans } from 'utils/i18n';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import {
  StyledFormContainer,
  StyledFormControl,
  StyledSwitchToLoginBox,
  StyledRegisterMain,
  StyledSwitchToLoginButton,
} from 'pages/Register/Register.style';
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
} from '@mui/material';
import { PATH } from 'routes/routeConfig';
import * as yup from 'yup';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { IRegisterInfo } from 'types/register';
import { Mui, useResponsive } from '@onedesign/ui';
import { useLoadingService } from 'services/LoadingService';
import { useSnackbar } from 'hooks/index';
import axios from 'plugins/api/axios';
import React, { useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { Icon, SvgName } from 'components/Icon';
import COUNTRY_CODES from 'assets/country_codes.json';
import { PersonalDataProtectionActDocument } from 'pages/Register/PDPADocument';
import { PolicyPrivacyDocument } from 'pages/Register/PolicyPrivacyDocument';
const { teacherRecruit, memberCenter } = axios;

const { Button, TextField, FormHelperText, Typography } = Mui;

const nameRegex = /^[A-Za-z0-9]+$/;
const nameInvalid = /^\d/;
const passwordRegex = /^(?=.*[a-zA-Z])(?=.*\d)[a-zA-Z\d]{8,12}$/;
const phoneRegex =
  /^((\\+[1-9]{1,4}[ \\-]*)|(\\([0-9]{2,3}\\)[ \\-]*)|([0-9]{2,4})[ \\-]*)*?[0-9]{3,4}?[ \\-]*[0-9]{3,4}?$/;

export interface IRegisterFormProps {
  onSubmit: (data: IRegisterInfo) => void;
}

const RegisterForm = ({ onSubmit }: IRegisterFormProps) => {
  const passwordMessage = trans(
    'register.validation.passwordMessage',
    'Please enter 8-12 alphanumeric characters, upper and lower cases are considered different',
  );

  const schema = yup
    .object()
    .shape({
      surName: yup
        .string()
        .required(
          trans('register.validation.surNameRequired', 'Surname is required'),
        ),
      firstName: yup
        .string()
        .required(
          trans(
            'register.validation.firstNameRequired',
            'First name is required',
          ),
        ),

      email: yup
        .string()
        .email(
          trans(
            'register.validation.emailFormatInvalid',
            'Please enter a valid email address',
          ),
        )
        .required(
          trans('register.validation.emailRequired', 'Email is required'),
        ),
      telephoneCountryCode: yup
        .string()
        .transform(value => value.trim())
        .required(
          trans(
            'register.validation.phoneCodeRequired',
            'Phone code is required',
          ),
        ),
      telephone: yup
        .string()
        .transform(value => value.trim())
        .matches(
          phoneRegex,
          trans(
            'register.validation.phoneNumberFormatInvalid',
            'Phone number is invalid',
          ),
        )
        .required(
          trans(
            'register.validation.phoneNumberRequired',
            'Phone number is required',
          ),
        ),
      username: yup
        .string()
        .required(
          trans(
            'register.validation.accountRequired',
            'Member account is required',
          ),
        )
        .test(
          'is-valid-username',
          trans(
            'register.validation.userNameInvalid',
            'The account ID should only start with alphabets',
          ),
          value => {
            if (value) return !nameInvalid.test(value);
            return false;
          },
        )
        .matches(
          nameRegex,
          trans(
            'register.validation.accountFormatInvalid',
            'Invalid member account format. Please use only letters(a-z) and digits',
          ),
        )
        .min(
          6,
          trans(
            'register.validation.accountTooShort',
            'Member account length should be 6 - 30 characters',
          ),
        )
        .max(
          30,
          trans(
            'register.validation.accountTooLong',
            'Member account length should be 6 - 30 characters',
          ),
        ),
      password: yup
        .string()
        .required(
          trans('register.validation.passwordRequired', 'Password is required'),
        )
        .matches(
          passwordRegex,
          trans(
            'register.validation.passwordFormatInvalid',
            'Please enter an 8-12 digits alphanumeric password',
          ),
        )
        .min(
          8,
          trans(
            'register.validation.passwordTooShort',
            'Please enter an 8-12 digits alphanumeric password',
          ),
        )
        .max(
          12,
          trans(
            'register.validation.passwordTooLong',
            'Please enter an 8-12 digits alphanumeric password',
          ),
        ),
      confirmPassword: yup
        .string()
        .required(
          trans('register.validation.passwordRequired', 'Password is required'),
        )
        .test(
          'confirm-password-not-match',
          trans(
            'register.validation.passwordMismatch',
            'Passwords do not match',
          ),
          (value, context) => {
            if (!value || value.trim().length === 0) {
              return true;
            }
            return value === context.parent.password;
          },
        ),
    })
    .required();

  const [isOpenDocument, setIsOpenDocument] = useState<boolean>(false);
  const [isOpenPrivacyPolicyDocument, setIsOpenPrivacyPolicyDocument] =
    useState<boolean>(false);
  const [isExistedUserName, setIsExistedUserName] = useState<boolean>(true);
  const { enqueueSnackbar } = useSnackbar();
  const { showLoading, hideLoading } = useLoadingService();
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const navigate = useNavigate();

  const methods = useForm({
    mode: 'onBlur',
    reValidateMode: 'onChange',
    resolver: yupResolver(schema),
    defaultValues: {
      surName: '',
      firstName: '',
      email: '',
      username: '',
      telephoneCountryCode: '+886',
      telephone: '',
      password: '',
      confirmPassword: '',
    },
  });
  const {
    handleSubmit,
    control,
    formState: { errors, isDirty, isValid },
    trigger,
    watch,
    setError,
  } = methods;

  const handleSubmitForm = async ({
    surName = '',
    firstName = '',
    email = '',
    username = '',
    telephoneCountryCode = '+886',
    telephone = '',
    password = '',
    confirmPassword = '',
  }: IRegisterInfo) => {
    if (
      surName &&
      firstName &&
      email &&
      username &&
      telephoneCountryCode &&
      telephone &&
      password &&
      confirmPassword
    ) {
      showLoading();
      try {
        const newTeacherRecruitment = await teacherRecruit.initTeacher({
          account: username,
          password,
          firstName,
          lastName: surName,
          telephoneCountryCode,
          telephone,
          gender: 'male',
          staffType: 'inside',
          status: TeacherStatus.UNVERIFIED,
          email,
        });
        if (newTeacherRecruitment.status !== EApiStatus.SUCCESS) {
          enqueueSnackbar(
            trans(
              'register.errorRegTeacher',
              'Registration failed, please register again',
            ),
            { variant: 'error' },
          );
          return;
        }
        onSubmit({
          surName,
          email,
          username,
          telephoneCountryCode,
          telephone,
          password,
          confirmPassword,
          firstName,
        });
      } catch (e) {
        enqueueSnackbar(
          trans(
            'register.errorInitTeacher',
            'Something went wrong, please try again later!',
          ),
          { variant: 'error' },
        );
      } finally {
        hideLoading();
      }
    }
  };

  const checkUsernameExists = async (value: string): Promise<boolean> => {
    const hasFieldError = errors.username && errors.username.type !== 'manual';
    if (hasFieldError) return false;

    const { err: availabilityErr, data: availabilityData } =
      await memberCenter.checkAvailability({ account: value });

    if (availabilityErr !== EMemberCenterErrorCode.SUCCESS) {
      return true; // 查詢失敗不代表帳號已存在，避免誤導使用者，交給送出時的後端驗證把關
    }

    if (!availabilityData?.account) {
      return false;
    }

    const { data } = await teacherRecruit.checkExistsAccount({
      type: 'username',
      value,
    });
    const exist = data?.exist || false;
    if (exist) {
      return false;
    }
    return true;
  };

  const currentPassword = watch('password');
  useEffect(() => {
    if (watch('confirmPassword')) trigger('confirmPassword');
  }, [currentPassword]);
  const [passwordShow, setPasswordShow] = useState(false);
  const [rePasswordShow, setRePasswordShow] = useState(false);

  const handlePasswordShow = () => {
    setPasswordShow(!passwordShow);
  };

  const handleRePasswordShow = () => {
    setRePasswordShow(!rePasswordShow);
  };
  const debouncedTriggerUsername = debounce(async (value: string) => {
    if (!value || value.trim().length < 0) {
      setIsExistedUserName(false);
    }

    const notExists = await checkUsernameExists(value);

    if (notExists) {
      setIsExistedUserName(true);
    } else {
      setIsExistedUserName(false);
    }
    trigger('username');
    return notExists;
  }, 300);

  if (!isExistedUserName) {
    if (!errors.username) {
      setError('username', {
        type: 'manual',
        message: trans(
          'register.validation.accountExists',
          'Username already exists',
        ),
      });
    }
  }

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
    onFieldChange: (value: string) => void,
  ) => {
    const inputValue = (event.target as HTMLInputElement).value;
    const transformedValue = inputValue.replace(/^0|[\s-]/g, '');
    onFieldChange(transformedValue);
  };

  const isDesktop = useResponsive('up', 'lg');

  const onCloseDocument = () => setIsOpenDocument(!isOpenDocument);

  const togglePrivacyPolicyDocument = () =>
    setIsOpenPrivacyPolicyDocument(!isOpenPrivacyPolicyDocument);

  const onConfirmPrivacyPolicyDocument = async (data: IRegisterInfo) => {
    togglePrivacyPolicyDocument();
    await handleSubmitForm(data);
  };

  const onConfirmDocument = () => {
    onCloseDocument();
    togglePrivacyPolicyDocument();
  };

  const setDocumentBeingShow = () => {
    if (isChecked) {
      togglePrivacyPolicyDocument();
    } else {
      setIsOpenDocument(true);
    }
  };

  return (
    <>
      <StyledRegisterMain>
        <h2>{trans('register.teacherRegister', 'Register account')}</h2>
        <FormProvider {...methods}>
          <StyledFormContainer>
            <form>
              <Box sx={{ display: 'flex', gap: '16px' }}>
                <StyledFormControl>
                  <Controller
                    name="surName"
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <TextField
                            {...field}
                            placeholder={trans(
                              'register.surNamePlaceholder',
                              'Surname*',
                            )}
                            fullWidth
                            error={!!errors.surName}
                          />
                          {errors.surName && (
                            <FormHelperText
                              sx={{
                                color: theme => theme.palette.error.main,
                              }}
                            >
                              {errors.surName.message}
                            </FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </StyledFormControl>
                <StyledFormControl>
                  <Controller
                    name="firstName"
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <TextField
                            {...field}
                            placeholder={trans(
                              'register.firstNamePlaceholder',
                              'First name*',
                            )}
                            fullWidth
                            error={!!errors.firstName}
                          />
                          {errors.firstName && (
                            <FormHelperText
                              sx={{
                                color: theme => theme.palette.error.main,
                              }}
                            >
                              {errors.firstName.message}
                            </FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </StyledFormControl>
              </Box>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ margin: '8px 0 0 16px' }}
              >
                後續若簽約將援引此處註冊資料，請務必填寫真實姓名
              </Typography>
              <StyledFormControl>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => {
                    return (
                      <>
                        <TextField
                          {...field}
                          placeholder={trans(
                            'register.emailPlaceholder',
                            'Email*',
                          )}
                          fullWidth
                          error={!!errors.email}
                        />
                        {errors.email && (
                          <FormHelperText
                            sx={{
                              color: theme => theme.palette.error.main,
                            }}
                          >
                            {errors.email.message}
                          </FormHelperText>
                        )}
                      </>
                    );
                  }}
                />
              </StyledFormControl>
              <Box sx={{ display: 'flex', gap: '16px' }}>
                <StyledFormControl sx={{ width: '40%' }}>
                  <Controller
                    name="telephoneCountryCode"
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <TextField
                            required
                            select
                            {...field}
                            label={trans(
                              'register.telephoneCountryCodeLabel',
                              'Phone code',
                            )}
                            placeholder={trans(
                              'register.phoneCodePlaceholder',
                              'Phone code',
                            )}
                            error={!!errors.telephoneCountryCode}
                          >
                            {COUNTRY_CODES.data.map(
                              ({ dial_code: dialCode, code, name }) => (
                                <MenuItem key={code} value={dialCode}>
                                  {isDesktop ? name : ''} ({dialCode})
                                </MenuItem>
                              ),
                            )}
                          </TextField>
                          {errors.telephoneCountryCode && (
                            <FormHelperText
                              sx={{
                                color: theme => theme.palette.error.main,
                              }}
                            >
                              {errors.telephoneCountryCode.message}
                            </FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </StyledFormControl>
                <StyledFormControl sx={{ width: '60%' }}>
                  <Controller
                    name="telephone"
                    control={control}
                    render={({ field }) => {
                      return (
                        <>
                          <TextField
                            required
                            {...field}
                            onChange={(
                              event: React.ChangeEvent<HTMLInputElement>,
                            ) => handleInputChange(event, field.onChange)}
                            placeholder={trans(
                              'register.phoneNumberPlaceholder',
                              'Phone number',
                            )}
                            fullWidth
                            error={!!errors.telephone}
                            type="tel"
                            inputProps={{
                              maxLength: 10,
                            }}
                          />
                          {errors.telephone && (
                            <FormHelperText
                              sx={{
                                color: theme => theme.palette.error.main,
                              }}
                            >
                              {errors.telephone.message}
                            </FormHelperText>
                          )}
                        </>
                      );
                    }}
                  />
                </StyledFormControl>
              </Box>

              <StyledFormControl>
                <Controller
                  name="username"
                  control={control}
                  render={({ field }) => {
                    return (
                      <>
                        <TextField
                          {...field}
                          placeholder={trans(
                            'register.accountPlaceholder',
                            'Account*',
                          )}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>,
                          ) => {
                            field.onChange(e.target.value);
                            if (e.target.value.trim().length > 0)
                              debouncedTriggerUsername(e.target.value);
                          }}
                          fullWidth
                          error={!!errors.username}
                        />
                        {errors.username && (
                          <FormHelperText
                            sx={{
                              color: theme => theme.palette.error.main,
                            }}
                          >
                            {errors.username.message}
                          </FormHelperText>
                        )}
                      </>
                    );
                  }}
                />
              </StyledFormControl>
              <StyledFormControl>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => {
                    return (
                      <>
                        <TextField
                          {...field}
                          type={passwordShow ? 'text' : 'password'}
                          placeholder={trans(
                            'register.passwordPlaceholder',
                            'Password*',
                          )}
                          fullWidth
                          error={!!errors.password}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handlePasswordShow}
                                  edge="end"
                                >
                                  {passwordShow ? (
                                    <Icon name={SvgName.EyeOn} />
                                  ) : (
                                    <Icon name={SvgName.EyeOff} />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {errors.password ? (
                          <FormHelperText
                            sx={{
                              color: theme => theme.palette.error.main,
                            }}
                          >
                            {errors.password.message}
                          </FormHelperText>
                        ) : (
                          <FormHelperText
                            sx={{
                              color: theme => theme.palette.grey[600],
                            }}
                          >
                            {passwordMessage}
                          </FormHelperText>
                        )}
                      </>
                    );
                  }}
                />
              </StyledFormControl>
              <StyledFormControl>
                <Controller
                  name="confirmPassword"
                  control={control}
                  render={({ field }) => {
                    return (
                      <>
                        <TextField
                          {...field}
                          type={rePasswordShow ? 'text' : 'password'}
                          placeholder={trans(
                            'register.confirmPasswordPlaceholder',
                            'Password confirmation*',
                          )}
                          fullWidth
                          error={!!errors.confirmPassword}
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton
                                  aria-label="toggle password visibility"
                                  onClick={handleRePasswordShow}
                                  edge="end"
                                >
                                  {rePasswordShow ? (
                                    <Icon name={SvgName.EyeOn} />
                                  ) : (
                                    <Icon name={SvgName.EyeOff} />
                                  )}
                                </IconButton>
                              </InputAdornment>
                            ),
                          }}
                        />
                        {errors.confirmPassword && (
                          <FormHelperText
                            sx={{
                              color: theme => theme.palette.error.main,
                            }}
                          >
                            {errors.confirmPassword.message}
                          </FormHelperText>
                        )}
                      </>
                    );
                  }}
                />
              </StyledFormControl>
              <Button
                id="btn-register"
                disabled={!isDirty || !isValid}
                sx={{ padding: '12px 0', marginTop: '2rem' }}
                variant="contained"
                onClick={() => setDocumentBeingShow()}
              >
                {trans('register.registerButton', 'Register')}
              </Button>
            </form>
          </StyledFormContainer>
        </FormProvider>
        <StyledSwitchToLoginBox>
          <Divider sx={{ width: '100%' }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'end',
            }}
          >
            {trans('register.callToLoginLabel', 'Already have an account？')}
            <StyledSwitchToLoginButton
              onClick={() => navigate(PATH.LOGIN, { replace: true })}
              variant="text"
            >
              {trans('register.callToLoginButton', 'Sign in')}
            </StyledSwitchToLoginButton>
          </Box>
        </StyledSwitchToLoginBox>
      </StyledRegisterMain>
      <PersonalDataProtectionActDocument
        openDocument={isOpenDocument}
        closeDocument={onCloseDocument}
        confirmDocument={handleSubmit(onConfirmDocument)}
        isChecked={isChecked}
        setIsChecked={setIsChecked}
      />
      <PolicyPrivacyDocument
        openDocument={isOpenPrivacyPolicyDocument}
        closeDocument={togglePrivacyPolicyDocument}
        confirmDocument={handleSubmit(onConfirmPrivacyPolicyDocument)}
      />
    </>
  );
};

export default RegisterForm;
